import { Link, useLocation } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import { mono } from '../styles/fonts';
import { useAuth } from '../context/AuthContext';

const navLinks = [
  { label: 'About', href: '/about' },
  { label: 'Trails', href: '/trails' },
  { label: 'Blog', href: '/blog' },
  { label: 'Pricing', href: '/pricing' },
];

export default function PublicNav({ activePath }: { activePath?: string }) {
  const { session } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const navRef = useRef<HTMLElement>(null);
  const location = useLocation();

  // Close menu on route change
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  // Close menu when clicking outside
  useEffect(() => {
    if (!menuOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (navRef.current && !navRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [menuOpen]);

  return (
    <nav
      ref={navRef}
      aria-label="Main navigation"
      className="bg-paper-bg border-b border-outline-variant/10 sticky top-0 z-50"
    >
      {/* Main bar */}
      <div className="w-full px-4 md:px-gutter max-w-[1200px] mx-auto h-14 flex items-center justify-between gap-6">

        {/* Logo */}
        <Link
          to="/"
          aria-label="Learnex home"
          className="inline-flex items-center gap-2"
        >
          <img src="/logo/logo-primary.svg" alt="Learnex" className="h-8 w-auto" width="120" height="32" />
        </Link>

        {/* Center nav links (desktop only) */}
        <div className="hidden md:flex items-center gap-1" role="list">
          {navLinks.map((item) => {
            const isActive = activePath && item.href === activePath;
            return (
              <Link
                key={item.label}
                to={item.href}
                role="listitem"
                aria-current={isActive ? 'page' : undefined}
                className={`px-4 py-1.5 rounded text-sm transition-colors ${
                  isActive
                    ? 'text-primary-container font-semibold'
                    : 'text-on-surface-variant hover:text-on-surface'
                }`}
                style={mono}
              >
                {item.label.toUpperCase()}
              </Link>
            );
          })}
        </div>

        {/* Right side (desktop): Sign in / Dashboard */}
        <div className="hidden md:flex items-center gap-3 shrink-0">
          <Link
            to={session ? '/app/dashboard' : '/login'}
            className="inline-flex items-center gap-2 bg-primary-container text-white px-5 py-1.5 rounded-full text-sm font-semibold hover:bg-primary-container/90 transition-colors"
            style={mono}
          >
            {session ? 'DASHBOARD' : 'SIGN IN'}
          </Link>
        </div>

        {/* Mobile right side: hamburger */}
        <button
          className="md:hidden inline-flex items-center justify-center w-10 h-10 rounded text-on-surface-variant hover:text-on-surface hover:bg-surface-container transition-colors"
          aria-label={menuOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={menuOpen}
          aria-controls="mobile-menu"
          onClick={() => setMenuOpen((v) => !v)}
        >
          {menuOpen ? (
            /* X icon */
            <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          ) : (
            /* Hamburger icon */
            <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="3" y1="6" x2="21" y2="6" />
              <line x1="3" y1="12" x2="21" y2="12" />
              <line x1="3" y1="18" x2="21" y2="18" />
            </svg>
          )}
        </button>
      </div>

      {/* Mobile dropdown menu */}
      <div
        id="mobile-menu"
        className={`md:hidden overflow-hidden transition-all duration-200 ease-in-out ${
          menuOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="px-4 pb-4 pt-1 flex flex-col gap-1 border-t border-outline-variant/10 bg-paper-bg">
          {navLinks.map((item) => {
            const isActive = activePath && item.href === activePath;
            return (
              <Link
                key={item.label}
                to={item.href}
                aria-current={isActive ? 'page' : undefined}
                className={`px-4 py-3 rounded text-sm transition-colors ${
                  isActive
                    ? 'text-primary-container font-semibold bg-surface-container'
                    : 'text-on-surface-variant hover:text-on-surface hover:bg-surface-container'
                }`}
                style={mono}
              >
                {item.label.toUpperCase()}
              </Link>
            );
          })}

          {/* Mobile CTA */}
          <Link
            to={session ? '/app/dashboard' : '/login'}
            className="mt-2 inline-flex items-center justify-center bg-primary-container text-white px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-primary-container/90 transition-colors"
            style={mono}
          >
            {session ? 'DASHBOARD' : 'SIGN IN'}
          </Link>
        </div>
      </div>
    </nav>
  );
}